import type { SlateTextOp } from 'src/models/crdtV2'

import { TranscriptParagraph, TranscriptSpeaker } from './Transcript'

/**
 * Operation to add a speaker to the transcript.
 */
export type TranscriptAddSpeakerOp = {
    type: 'add_speaker'
    id?: string
    speaker: Omit<TranscriptSpeaker, 'id'>
}

/**
 * Operation to edit a speaker in the transcript.
 */
export type TranscriptEditSpeakerOp = {
    type: 'edit_speaker'
    id: string
    speaker: Omit<TranscriptSpeaker, 'id'>
}

/**
 * Operation to set attributes of a paragraph.
 */
export type TranscriptSetParagraphOp = {
    type: 'set_paragraph'
    paragraphId: string
    paragraph: Partial<Pick<TranscriptParagraph, 'speakerId'>>
}

/**
 * Insert a `paragraph_change` at the caret. `atIndex` counts finalized characters only.
 * The new marker's id is `newParagraphId`; its speaker and QAC are copied from `paragraphId`.
 */
export type TranscriptSplitParagraphOp = {
    type: 'split_paragraph'
    paragraphId: string
    atIndex: number
    newParagraphId: string
}

/** Which neighbor a paragraph merges with. */
export enum ParagraphMergeDirection {
    Previous = -1,
    Next = 1,
}

/**
 * Delete the boundary marker toward the previous or next paragraph.
 * The surviving marker keeps its speaker and QAC.
 */
export type TranscriptMergeParagraphOp = {
    type: 'merge_paragraph'
    paragraphId: string
    direction: ParagraphMergeDirection
}

/** One finalized-text edit. `edit.at` counts finalized characters only. */
export type TranscriptParagraphTextOp = {
    type: 'paragraph_text'
    paragraphId: string
    edit: SlateTextOp
}

/**
 * A TranscriptOp is a single operation to be applied to the transcript.
 */
export type TranscriptOp =
    | TranscriptAddSpeakerOp
    | TranscriptEditSpeakerOp
    | TranscriptSetParagraphOp
    | TranscriptSplitParagraphOp
    | TranscriptMergeParagraphOp
    | TranscriptParagraphTextOp
